import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Upload, CheckCircle, Camera, ArrowRight, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [cmsContent, setCmsContent] = useState({});
  const [cmsLoading, setCmsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    description: '',
    rightsConfirmed: false
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    fetchCmsContent();
  }, []);

  const fetchCmsContent = async () => {
    try {
      const response = await axios.get(`${API}/cms`);
      setCmsContent(response.data);
    } catch (error) {
      console.error('Failed to fetch CMS content:', error);
    } finally {
      setCmsLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')
    );
    
    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rightsConfirmed) {
      toast.error('Bitte bestätige die Rechteübertragung');
      return;
    }
    
    if (files.length === 0) {
      toast.error('Bitte wähle mindestens ein Bild aus');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('description', formData.description || '');
    formDataToSend.append('rights_confirmed', formData.rightsConfirmed);
    
    files.forEach(file => {
      formDataToSend.append('files', file);
    });

    try {
      await axios.post(`${API}/upload`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      setUploadSuccess(true);
      toast.success('Bilder erfolgreich hochgeladen!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Upload fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', description: '', rightsConfirmed: false });
    setFiles([]);
    setUploadSuccess(false);
    setUploadProgress(0);
    setShowUploadForm(false);
  };

  // CMS Content with fallbacks - using exact field names from Mietwagen
  const heroKoop = cmsContent.hero_koop || 'Dein Blick auf Sylt';
  const heroTitle = cmsContent.hero_title || 'Teile deine Sylt-Momente mit GB Sylt';
  const heroSub = cmsContent.hero_sub || 'Du hast besondere Urlaubsbilder von Sylt aufgenommen? Dann lade deine schönsten Eindrücke ganz einfach hier hoch. Mit etwas Glück erscheinen deine Fotos später auf unserer Website oder in unseren Social Media Kanälen.';
  const heroButton = cmsContent.hero_button || 'Jetzt Bilder hochladen';
  const heroBoxTitle = cmsContent.hero_box_title || 'Schnell online';
  const heroBoxText = cmsContent.hero_box_text || 'Einfach Bilder auswählen und direkt hochladen';
  const heroPickup = cmsContent.hero_pickup || 'Bilder direkt hochladen und für GB Sylt zur Verfügung stellen.';
  const heroHint = cmsContent.hero_hint || 'Mit dem Hochladen bestätigst du, dass du die Rechte an den Bildern hast und wir diese verwenden dürfen.';
  // Hero-Bild: Verwende CMS-Bild über Serve-API (kein Fallback-Bild um Flackern zu vermeiden)
  const bgImage = cmsContent.hero_image 
    ? `${process.env.REACT_APP_BACKEND_URL}/api/cms/serve-image/hero_image`
    : null;

  // Success Page
  if (uploadSuccess) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>
        <header className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0E518D] rounded flex items-center justify-center">
                  <Camera className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-xl font-bold text-[#0E518D]">GB Sylt</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em]">Bilddatenbank</div>
                </div>
              </Link>
            </div>
          </div>
        </header>

        <div className="flex items-center justify-center min-h-[80vh] px-4">
          <div className="max-w-lg text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-[#0E518D] mb-4">
              {cmsContent.upload_success_title || 'Vielen Dank!'}
            </h1>
            <p className="text-gray-600 mb-8 leading-relaxed">
              {cmsContent.upload_success_text || 'Deine Bilder wurden erfolgreich hochgeladen. Wir freuen uns über deinen Beitrag!'}
            </p>
            <button 
              onClick={resetForm}
              className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm px-8 py-4 font-medium tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all duration-300 inline-flex items-center gap-2"
              data-testid="upload-more-button"
            >
              Weitere Bilder hochladen
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Upload Form
  if (showUploadForm) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>
        <header className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" onClick={() => setShowUploadForm(false)} className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0E518D] rounded flex items-center justify-center">
                  <Camera className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-xl font-bold text-[#0E518D]">GB Sylt</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em]">Bilddatenbank</div>
                </div>
              </Link>
              <button
                onClick={() => setShowUploadForm(false)}
                className="text-gray-500 hover:text-[#0E518D] text-sm"
              >
                ← Zurück zur Startseite
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <h1 className="text-3xl font-bold text-[#0E518D] mb-2">Bilder hochladen</h1>
          <p className="text-gray-600 mb-8">Teile deine schönsten Sylt-Momente mit uns.</p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">Name *</Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Dein Name"
                  className="border-gray-300 focus:border-[#0E518D] focus:ring-[#0E518D]"
                  data-testid="upload-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="deine@email.de"
                  className="border-gray-300 focus:border-[#0E518D] focus:ring-[#0E518D]"
                  data-testid="upload-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">Beschreibung (optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Erzähle uns etwas über deine Bilder..."
                rows={3}
                className="border-gray-300 focus:border-[#0E518D] focus:ring-[#0E518D]"
                data-testid="upload-description-input"
              />
            </div>

            {/* Upload Zone */}
            <div
              className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
                dragActive ? 'border-[#0E518D] bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="upload-dropzone"
            >
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/jpeg,image/png,image/heic,image/heif"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="upload-file-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-8 h-8 text-[#0E518D]" />
                </div>
                <p className="text-gray-700 font-medium mb-1">
                  Bilder hier ablegen oder klicken
                </p>
                <p className="text-sm text-gray-500">
                  JPG, PNG, HEIC • Maximal 20 MB pro Bild
                </p>
              </label>
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  {files.length} Bild{files.length !== 1 ? 'er' : ''} ausgewählt
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {files.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-full h-20 object-cover rounded-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                        data-testid={`remove-file-${index}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-2">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-sm text-gray-600 text-center">{uploadProgress}% hochgeladen</p>
              </div>
            )}

            {/* Rights Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-sm">
              <Checkbox
                id="rights"
                checked={formData.rightsConfirmed}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rightsConfirmed: checked }))}
                data-testid="upload-rights-checkbox"
              />
              <Label htmlFor="rights" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                {cmsContent.rights_checkbox_text || 'Mit dem Absenden bestätigst du, dass du die Rechte an den hochgeladenen Bildern hast. Gleichzeitig erlaubst du der GB Sylt GmbH, die Bilder für Werbung, Website und Social Media zu verwenden und zu veröffentlichen.'}
              </Label>
            </div>

            <button 
              type="submit" 
              className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm py-4 font-medium tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              disabled={uploading || !formData.name || !formData.email || files.length === 0 || !formData.rightsConfirmed}
              data-testid="upload-submit-button"
            >
              {uploading ? 'Wird hochgeladen...' : 'Bilder absenden'}
              {!uploading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-200 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-500">
                {cmsContent.footer_text || '© GB Sylt GmbH. Alle Rechte vorbehalten.'}
              </p>
              <div className="flex gap-6 text-sm">
                <a href={cmsContent.footer_privacy_link || 'https://gb-sylt.de/datenschutz'} className="text-gray-500 hover:text-[#0E518D]" target="_blank" rel="noopener noreferrer">
                  Datenschutz
                </a>
                <a href={cmsContent.footer_imprint_link || 'https://gb-sylt.de/impressum'} className="text-gray-500 hover:text-[#0E518D]" target="_blank" rel="noopener noreferrer">
                  Impressum
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Main Landing Page - EXACT Mietwagen Structure
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <header className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#0E518D] rounded flex items-center justify-center">
                <Camera className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="text-xl font-bold text-[#0E518D]">GB Sylt</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em]">Bilddatenbank</div>
              </div>
            </Link>
            
            {/* Header CTA */}
            <div className="hidden sm:flex items-center gap-4">
              <Link to="/admin/login">
                <span className="text-sm text-gray-500 hover:text-[#0E518D]" data-testid="admin-login-header">Admin</span>
              </Link>
              <button 
                onClick={() => setShowUploadForm(true)}
                className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm px-6 py-3 text-sm font-medium tracking-wide uppercase shadow-md hover:shadow-lg transition-all duration-300"
                data-testid="header-upload-button"
              >
                Jetzt Bilder hochladen
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - EXACT Mietwagen Structure */}
      <section data-testid="hero-section" className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-12 md:py-20 lg:py-24">
            
            {/* LEFT AREA - Exact Mietwagen Structure */}
            <div className="order-2 lg:order-1">
              
              {/* Kooperations-/Hinweis-Pill */}
              <div className="inline-flex items-center gap-2 bg-[#0E518D]/[0.08] text-[#0E518D] px-4 py-2 rounded-full text-sm font-medium mb-6 opacity-0 animate-fade-in-up">
                <Camera className="w-4 h-4" />
                <span>{heroKoop}</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-5 opacity-0 animate-fade-in-up animate-delay-100">
                {heroTitle}
              </h1>

              {/* Description */}
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 max-w-xl opacity-0 animate-fade-in-up animate-delay-200">
                {heroSub}
              </p>

              {/* Button + Infobox Wrapper */}
              <div className="flex flex-col sm:flex-row items-start gap-4 mb-6 opacity-0 animate-fade-in-up animate-delay-300">
                
                {/* Main Button */}
                <button 
                  onClick={() => setShowUploadForm(true)}
                  className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm px-8 py-4 font-medium tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all duration-300 inline-flex items-center gap-2"
                  data-testid="hero-upload-button"
                >
                  {heroButton}
                  <ArrowRight className="w-4 h-4" />
                </button>

                {/* Infobox */}
                <div className="flex items-center gap-2 px-4 py-2.5 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-sm">
                  <span className="text-[#0E518D] font-bold text-sm">
                    {heroBoxTitle}
                  </span>
                  <span className="text-slate-500 text-xs">
                    {heroBoxText}
                  </span>
                </div>

              </div>

              {/* Pickup/Location hint */}
              <div className="flex items-center gap-2 text-slate-500 text-sm opacity-0 animate-fade-in-up animate-delay-400">
                <MapPin className="w-4 h-4" />
                <span>{heroPickup}</span>
              </div>

              {/* Additional hint text */}
              <p className="text-slate-500 text-xs mt-3 italic opacity-0 animate-fade-in-up animate-delay-400">
                {heroHint}
              </p>

            </div>

            {/* RIGHT AREA - Hero Image */}
            <div className="order-1 lg:order-2 opacity-0 animate-fade-in-up animate-delay-200">
              <div className="relative">
                
                <div className="rounded-sm overflow-hidden shadow-xl">
                  {cmsLoading ? (
                    /* Ladezustand: Markenfarbe als Hintergrund */
                    <div 
                      className="w-full h-64 sm:h-80 lg:h-[420px] bg-[#0E518D] flex items-center justify-center"
                      data-testid="hero-image-loading"
                    >
                      <div className="animate-pulse">
                        <Camera className="w-16 h-16 text-white/50" />
                      </div>
                    </div>
                  ) : bgImage ? (
                    /* CMS-Bild vorhanden */
                    <img
                      src={bgImage}
                      alt="Bilder hochladen für GB Sylt"
                      className="w-full h-64 sm:h-80 lg:h-[420px] object-cover"
                      data-testid="hero-image"
                    />
                  ) : (
                    /* Kein CMS-Bild: Markenfarbe als Standard-Hintergrund */
                    <div 
                      className="w-full h-64 sm:h-80 lg:h-[420px] bg-[#0E518D] flex items-center justify-center"
                      data-testid="hero-image-placeholder"
                    >
                      <Camera className="w-20 h-20 text-white/30" />
                    </div>
                  )}
                </div>

                {/* Decorative elements */}
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#0E518D]/10 rounded-sm -z-10" />
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#D4AF37]/10 rounded-sm -z-10" />

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Mobile CTA */}
      <div className="sm:hidden px-4 pb-8">
        <button 
          onClick={() => setShowUploadForm(true)}
          className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm py-4 font-medium tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all duration-300 inline-flex items-center justify-center gap-2"
        >
          {heroButton}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Features Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0E518D] text-center mb-12">So funktioniert's</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                <span className="text-2xl font-bold text-[#0E518D]">01</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Bilder auswählen</h3>
              <p className="text-slate-600 text-sm">Wähle deine schönsten Sylt-Fotos aus. Mehrere Bilder gleichzeitig möglich.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                <span className="text-2xl font-bold text-[#0E518D]">02</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Kontakt angeben</h3>
              <p className="text-slate-600 text-sm">Gib deinen Namen und deine E-Mail-Adresse an.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                <span className="text-2xl font-bold text-[#0E518D]">03</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Hochladen</h3>
              <p className="text-slate-600 text-sm">Bestätige die Nutzungsrechte und sende deine Bilder ab.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0E518D] text-center mb-8">Häufig gestellte Fragen</h2>
          
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="item-1" className="border border-gray-200 rounded-sm px-4">
              <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline" data-testid="faq-item-1">
                Welche Bildformate werden akzeptiert?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Wir akzeptieren JPG, PNG und HEIC Dateien. Die maximale Dateigröße beträgt 20 MB pro Bild.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="border border-gray-200 rounded-sm px-4">
              <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline" data-testid="faq-item-2">
                Wie werden meine Bilder verwendet?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Deine Bilder können für Marketing, Website und Social Media der GB Sylt GmbH verwendet werden. Die Originalbilder werden sicher gespeichert.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="border border-gray-200 rounded-sm px-4">
              <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline" data-testid="faq-item-3">
                Kann ich mehrere Bilder gleichzeitig hochladen?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Ja! Du kannst mehrere Bilder auf einmal auswählen oder per Drag & Drop in das Upload-Feld ziehen.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="border border-gray-200 rounded-sm px-4">
              <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline" data-testid="faq-item-4">
                Werden meine Kontaktdaten gespeichert?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Deine Kontaktdaten werden zusammen mit den Bildern gespeichert, um den Urheber zuordnen zu können. Die Daten werden nicht an Dritte weitergegeben.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#0E518D] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Jetzt deine Sylt-Bilder hochladen</h2>
          <p className="text-white/80 mb-6">Teile deine schönsten Momente mit uns.</p>
          <button 
            onClick={() => setShowUploadForm(true)}
            className="bg-white text-[#0E518D] hover:bg-gray-100 rounded-sm px-8 py-4 font-medium tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all duration-300 inline-flex items-center gap-2"
          >
            Bilder hochladen
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#0E518D] rounded flex items-center justify-center">
                <Camera className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-[#0E518D]">GB Sylt</span>
            </div>
            
            <p className="text-sm text-gray-500">
              {cmsContent.footer_text || '© GB Sylt GmbH. Alle Rechte vorbehalten.'}
            </p>
            
            <div className="flex gap-6 text-sm">
              <Link to="/datenschutz" className="text-gray-500 hover:text-[#0E518D]">
                Datenschutz
              </Link>
              <Link to="/impressum" className="text-gray-500 hover:text-[#0E518D]">
                Impressum
              </Link>
              <Link to="/agb" className="text-gray-500 hover:text-[#0E518D]">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
